import { apiClient, Page, GUEST_SEARCH_DEFAULT_LIMIT, GUEST_SEARCH_DEFAULT_OFFSET } from "rp-components"
import GuestInvite, { CreateGuestInvitePayload, FileRef, GuestInviteListItem, GuestUser, UpdateGuestInvitePayload } from "@/models/GuestInvite"

export interface GuestInviteService {
    createInvite(payload: CreateGuestInvitePayload): Promise<GuestInvite>
    searchGuests(search: string): Promise<GuestUser[]>
    getMyInvites(minPageSize: number): Promise<Page<GuestInviteListItem>>
    getMyInvitesNextPage(nextUrl: string): Promise<Page<GuestInviteListItem>>
    updateInvite(id: string, payload: UpdateGuestInvitePayload): Promise<GuestInvite>
    revokeInvite(id: string): Promise<GuestInvite>
    resendInvite(id: string): Promise<GuestInvite>
    getTenantAttachments(propertyIds: number[]): Promise<FileRef[]>
}

class GuestInviteServiceImpl implements GuestInviteService {
    static instance?: GuestInviteService = undefined

    private constructor() {}

    static getInstance(): GuestInviteService {
        if (GuestInviteServiceImpl.instance == undefined) {
            GuestInviteServiceImpl.instance = new GuestInviteServiceImpl()
        }
        return GuestInviteServiceImpl.instance
    }

    async createInvite(payload: CreateGuestInvitePayload): Promise<GuestInvite> {
        return await apiClient.post<GuestInvite, CreateGuestInvitePayload>("/guest-user/invitations", payload)
    }

    async searchGuests(search: string): Promise<GuestUser[]> {
        return await apiClient.get<GuestUser[]>("/guest-user/search", {
            search,
            limit: GUEST_SEARCH_DEFAULT_LIMIT,
            offset: GUEST_SEARCH_DEFAULT_OFFSET
        })
    }

    async getMyInvites(minPageSize: number): Promise<Page<GuestInviteListItem>> {
        return await apiClient.getPage<GuestInviteListItem>("/guest-user/invitations", { minPageSize })
    }

    async getMyInvitesNextPage(nextUrl: string): Promise<Page<GuestInviteListItem>> {
        return await apiClient.getNextPage<GuestInviteListItem>(nextUrl)
    }

    async updateInvite(id: string, payload: UpdateGuestInvitePayload): Promise<GuestInvite> {
        return await apiClient.patch<GuestInvite, UpdateGuestInvitePayload>(`/guest-user/invitations/${id}`, payload)
    }

    async revokeInvite(id: string): Promise<GuestInvite> {
        return await apiClient.post<GuestInvite, Record<string, never>>(`/guest-user/invitations/${id}/revoke`, {})
    }

    async resendInvite(id: string): Promise<GuestInvite> {
        return await apiClient.post<GuestInvite, Record<string, never>>(`/guest-user/invitations/${id}/resend`, {})
    }

    async getTenantAttachments(propertyIds: number[]): Promise<FileRef[]> {
        if (propertyIds.length === 0) return []
        const query = propertyIds.map((id) => `propertyId=${encodeURIComponent(String(id))}`).join("&")
        return await apiClient.get<FileRef[]>(`/guest-user/invitations/tenant-attachments?${query}`)
    }
}

export default GuestInviteServiceImpl.getInstance()
